import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Home, Pencil, X, Users, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { useStudentFriendships } from '../../hooks/useStudents';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { queryKeys } from '../../lib/queryClient';

export function HouseAssignmentPanel({ student, canEdit }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [isChanging, setIsChanging] = useState(false);
  const [showGroupConfirm, setShowGroupConfirm] = useState(false);
  const [pendingHouseUuid, setPendingHouseUuid] = useState(undefined);

  // Fetch allocation data for the student's course
  const { data: allocationData, isLoading: housesLoading } = useQuery({
    queryKey: ['allocation', student.course],
    queryFn: () => api.allocation.getData(student.course),
    enabled: !!student.course,
  });

  // Fetch friend group
  const { data: friendships } = useStudentFriendships(student.uuid);

  // Resolve group members from friendship data
  const groupMembers = useMemo(() => {
    if (!friendships || !Array.isArray(friendships)) return [];
    const others = new Map();
    for (const f of friendships) {
      if (f.student1 !== student.uuid) {
        others.set(f.student1, f.student1_name);
      }
      if (f.student2 !== student.uuid) {
        others.set(f.student2, f.student2_name);
      }
    }
    return Array.from(others.entries()).map(([uuid, name]) => ({ uuid, name }));
  }, [friendships, student.uuid]);

  const isInGroup = groupMembers.length > 0;
  const allGroupUuids = [student.uuid, ...groupMembers.map((m) => m.uuid)];

  const hasMixedGender = useMemo(() => {
    if (!isInGroup || !allocationData?.students) return false;
    return groupMembers.some((m) => {
      const entry = allocationData.students.find((s) => s.uuid === m.uuid);
      return entry && entry.gender !== student.gender;
    });
  }, [isInGroup, groupMembers, allocationData, student.gender]);

  // Houses filtered by gender
  const filteredHouses = useMemo(() => {
    if (!allocationData?.houses) return [];
    return allocationData.houses.filter(
      (h) => h.gender_designation === student.gender
    );
  }, [allocationData, student.gender]);

  // Move mutation
  const moveMutation = useMutation({
    mutationFn: ({ uuids, houseUuid }) => api.allocation.move(uuids, houseUuid),
    onSuccess: (_, variables) => {

      const isUnassign = variables.houseUuid === null;
      if (isInGroup && allGroupUuids.length > 1) {
        const houseName = variables.houseUuid
          ? filteredHouses.find((h) => h.uuid === variables.houseUuid)?.name || variables.houseUuid
          : null;
        toast({
          title: isUnassign
            ? t('students.housing.unassignSuccess')
            : t('students.housing.moveSuccess'),
          description: isUnassign
            ? null
            : t('students.housing.groupMovedDesc', {
                count: allGroupUuids.length,
                house: houseName,
              }),
          variant: 'success',
        });
      } else {
        toast({
          title: isUnassign
            ? t('students.housing.unassignSuccess')
            : t('students.housing.moveSuccess'),
          variant: 'success',
        });
      }

      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: queryKeys.students.detail(student.uuid) });
      queryClient.invalidateQueries({ queryKey: ['allocation', student.course] });
      queryClient.invalidateQueries({ queryKey: ['allocation', 'summary'] });

      setIsChanging(false);
      setShowGroupConfirm(false);
      setPendingHouseUuid(undefined);
    },
    onError: (error) => {
      const details = error?.response?.data?.details || [];
      const reason = details[0]?.reason;
      let description;
      if (reason === 'capacity') {
        description = t('students.housing.errorCapacity');
      } else if (reason === 'gender') {
        description = t('students.housing.errorGender');
      } else {
        description = error?.response?.data?.error;
      }
      toast({
        title: t('students.housing.moveError'),
        description,
        variant: 'destructive',
      });
    },
  });

  const initiateMove = (houseUuid) => {
    if (isInGroup) {
      setPendingHouseUuid(houseUuid ?? null);
      setShowGroupConfirm(true);
    } else {
      moveMutation.mutate({ uuids: [student.uuid], houseUuid: houseUuid ?? null });
    }
  };

  const confirmMove = () => {
    moveMutation.mutate({ uuids: allGroupUuids, houseUuid: pendingHouseUuid ?? null });
  };

  const cancelConfirm = () => {
    setShowGroupConfirm(false);
    setPendingHouseUuid(undefined);
  };

  // Determine pending house name for confirm dialog
  const pendingHouseName = useMemo(() => {
    if (!pendingHouseUuid) return null;
    return filteredHouses.find((h) => h.uuid === pendingHouseUuid)?.name || pendingHouseUuid;
  }, [pendingHouseUuid, filteredHouses]);

  // Friend group "together" check
  const groupTogetherStatus = useMemo(() => {
    if (!isInGroup || !allocationData?.students) return null;
    const getHouseForStudent = (uuid) => {
      const entry = allocationData.students.find((s) => s.uuid === uuid);
      return entry?.house_uuid || null;
    };
    const myHouse = getHouseForStudent(student.uuid);
    const allSameHouse =
      myHouse !== null &&
      allGroupUuids.every((uuid) => getHouseForStudent(uuid) === myHouse);
    return allSameHouse ? 'together' : 'split';
  }, [isInGroup, allocationData, allGroupUuids, student.uuid]);

  // Get house name for another student from allocation data
  const getStudentHouseName = (memberUuid) => {
    if (!allocationData?.students || !allocationData?.houses) return null;
    const entry = allocationData.students.find((s) => s.uuid === memberUuid);
    if (!entry || !entry.house_uuid) return null;
    const house = allocationData.houses.find((h) => h.uuid === entry.house_uuid);
    return house?.name || null;
  };

  return (
    <Card>
        <CardHeader className="bg-muted/40 border-b border-border py-3 px-4">
          <CardTitle className="flex items-center text-sm font-semibold text-foreground">
            <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#F28482]/10 mr-3">
              <Home className="h-4 w-4 text-[#F28482]" />
            </span>
            {t('students.housing.currentAssignment')}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          {!isChanging && !showGroupConfirm && (
            <div className="flex items-center justify-between">
              <div>
                {student.house ? (
                  <p className="font-medium">{student.house_name || student.house}</p>
                ) : (
                  <Badge variant="outline">
                    {t('students.housing.unassigned')}
                  </Badge>
                )}
                {student.course_name && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {student.course_name}
                  </p>
                )}
              </div>

              {canEdit && student.course && (
                <div className="flex items-center space-x-2">
                  {student.house && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => initiateMove(null)}
                      disabled={moveMutation.isPending}
                    >
                      <X className="h-4 w-4 mr-1" />
                      {isInGroup ? t('students.housing.removeGroup') : t('students.housing.remove')}
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsChanging(true)}
                    disabled={moveMutation.isPending}
                  >
                    <Pencil className="h-4 w-4 mr-1" />
                    {t('students.housing.changeHouse')}
                  </Button>
                </div>
              )}
            </div>
          )}

          {isChanging && !showGroupConfirm && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {t('students.housing.selectPrompt')}
              </p>

              {housesLoading ? (
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <span>{t('common.loading')}</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Select
                    onValueChange={(value) => {
                      if (value === '__unassigned__') {
                        initiateMove(null);
                      } else {
                        initiateMove(value);
                      }
                    }}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="— Unassigned —" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__unassigned__">
                        — {t('students.housing.unassigned')} —
                      </SelectItem>
                      {filteredHouses.map((house) => {
                        const isCurrent = house.uuid === student.house;
                        const spotsNeeded = allGroupUuids.filter((uuid) => {
                          const entry = allocationData?.students?.find((s) => s.uuid === uuid);
                          return !entry || entry.house_uuid !== house.uuid;
                        }).length;
                        const isFull = house.available_spots < spotsNeeded;
                        return (
                          <SelectItem
                            key={house.uuid}
                            value={house.uuid}
                            disabled={isFull && !isCurrent}
                          >
                            {house.name} · {house.current_occupancy}/
                            {house.course_capacity}
                            {isFull && !isCurrent
                              ? ` (${t('houseAllocation.full')})`
                              : ''}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-9 w-9 p-0 shrink-0 text-muted-foreground hover:text-foreground"
                    onClick={() => setIsChanging(false)}
                    aria-label={t('common.cancel')}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          )}

          {showGroupConfirm && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-4 space-y-3">
              <div className="flex items-start space-x-2">
                <Users className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-amber-900">
                    {pendingHouseUuid
                      ? t('students.housing.groupMoveConfirm', {
                          count: allGroupUuids.length,
                          house: pendingHouseName,
                        })
                      : t('students.housing.groupUnassignConfirm', {
                          count: allGroupUuids.length,
                        })}
                  </p>
                  <div className="text-xs text-amber-700 space-y-0.5">
                    <p className="font-medium">{t('students.housing.groupMembers')}:</p>
                    <p>
                      {student.first_name} {student.last_name}
                    </p>
                    {groupMembers.map((m) => (
                      <p key={m.uuid}>{m.name}</p>
                    ))}
                  </div>
                  {hasMixedGender && (
                    <p className="text-xs font-medium text-red-700 mt-1">
                      {t('students.housing.groupGenderWarning')}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  onClick={confirmMove}
                  disabled={moveMutation.isPending}
                >
                  {t('common.yes')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={cancelConfirm}
                  disabled={moveMutation.isPending}
                >
                  {t('common.cancel')}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

  );
}

export function FriendGroupCard({ student }) {
  const { t } = useTranslation();

  const { data: allocationData } = useQuery({
    queryKey: ['allocation', student.course],
    queryFn: () => api.allocation.getData(student.course),
    enabled: !!student.course,
  });

  const { data: friendships } = useStudentFriendships(student.uuid);

  const groupMembers = useMemo(() => {
    if (!friendships || !Array.isArray(friendships)) return [];
    const others = new Map();
    for (const f of friendships) {
      if (f.student1 !== student.uuid) others.set(f.student1, f.student1_name);
      if (f.student2 !== student.uuid) others.set(f.student2, f.student2_name);
    }
    return Array.from(others.entries()).map(([uuid, name]) => ({ uuid, name }));
  }, [friendships, student.uuid]);

  const allGroupUuids = [student.uuid, ...groupMembers.map((m) => m.uuid)];

  const groupTogetherStatus = useMemo(() => {
    if (!groupMembers.length || !allocationData?.students) return null;
    const getHouse = (uuid) => allocationData.students.find((s) => s.uuid === uuid)?.house_uuid ?? null;
    const myHouse = getHouse(student.uuid);
    return myHouse !== null && allGroupUuids.every((uuid) => getHouse(uuid) === myHouse)
      ? 'together'
      : 'split';
  }, [groupMembers, allocationData, allGroupUuids, student.uuid]);

  const getStudentHouseName = (memberUuid) => {
    if (!allocationData?.students || !allocationData?.houses) return null;
    const entry = allocationData.students.find((s) => s.uuid === memberUuid);
    if (!entry || !entry.house_uuid) return null;
    return allocationData.houses.find((h) => h.uuid === entry.house_uuid)?.name || null;
  };

  if (!groupMembers.length) return null;

  return (
    <Card>
      <CardHeader className="bg-muted/40 border-b border-border py-3 px-4">
        <CardTitle className="flex items-center justify-between text-sm font-semibold text-foreground">
          <div className="flex items-center">
            <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#F28482]/10 mr-3">
              <Users className="h-4 w-4 text-[#F28482]" />
            </span>
            {t('students.housing.friendGroup')}
          </div>
          {allocationData && groupTogetherStatus && (
            groupTogetherStatus === 'together' ? (
              <Badge className="bg-green-100 text-green-800 border-green-300">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                {t('students.housing.groupTogether')}
              </Badge>
            ) : (
              <Badge className="bg-amber-100 text-amber-800 border-amber-300">
                <AlertTriangle className="h-3 w-3 mr-1" />
                {t('students.housing.groupSplit')}
              </Badge>
            )
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 py-0">
        <ul className="divide-y divide-border">
          <li className="flex items-center justify-between py-3">
            <span className="text-sm">
              <span className="font-medium">{student.first_name} {student.last_name}</span>{' '}
              <span className="text-muted-foreground text-xs">{t('students.housing.youSuffix')}</span>
            </span>
            <span className="text-sm text-muted-foreground">
              {student.house_name || t('students.housing.unassigned')}
            </span>
          </li>
          {groupMembers.map((member) => {
            const memberHouseName = getStudentHouseName(member.uuid);
            return (
              <li key={member.uuid} className="flex items-center justify-between py-3 last:pb-3">
                <span className="text-sm">{member.name}</span>
                <span className="text-sm text-muted-foreground">
                  {memberHouseName !== null ? memberHouseName || t('students.housing.unassigned') : '—'}
                </span>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}

export default HouseAssignmentPanel;
